import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Calculator,
  ChevronLeft,
  ChevronRight,
  Eye,
  Loader2,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import {
  useGetRiskCalculationsPaged,
  useDeleteRiskCalculation,
} from '../hooks/RiskCalculation';
import { BaseRiskCalculation } from '../dtos/RiskCalculations/index';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

export default function RiskCalculations() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState('');
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<BaseRiskCalculation | null>(null);

  const page = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parseInt(searchParams.get('size') || '10', 10);
  const searchTerm = searchParams.get('search') || '';

  useEffect(() => {
    setSearchInput(searchTerm);
  }, [searchTerm]);

  const { data: response, isLoading } = useGetRiskCalculationsPaged(page, pageSize, searchTerm || undefined);
  const deleteMutation = useDeleteRiskCalculation();

  const pagedData = response?.isSuccess ? response.data : null;
  const items = pagedData?.items || [];
  const totalCount = pagedData?.totalCount || 0;
  const totalPages = pagedData?.totalPages || 1;

  const updateParams = (updates: { page?: number; size?: number; search?: string }) => {
    const newParams = new URLSearchParams(searchParams);
    if (updates.page !== undefined) {
      if (updates.page === 1) newParams.delete('page');
      else newParams.set('page', updates.page.toString());
    }
    if (updates.size !== undefined) {
      if (updates.size === 10) newParams.delete('size');
      else newParams.set('size', updates.size.toString());
    }
    if (updates.search !== undefined) {
      if (updates.search === '') newParams.delete('search');
      else newParams.set('search', updates.search);
      newParams.delete('page');
    }
    setSearchParams(newParams);
  };

  const handleSearch = () => updateParams({ search: searchInput });
  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };
  const clearSearch = () => {
    setSearchInput('');
    updateParams({ search: '' });
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    const res = await deleteMutation.mutateAsync(selectedItem.id);
    if (res.isSuccess) {
      setIsDeleteDialogOpen(false);
      setSelectedItem(null);
    }
  };

  const openViewDialog = (item: BaseRiskCalculation) => {
    setSelectedItem(item);
    setIsViewDialogOpen(true);
  };

  const openDeleteDialog = (item: BaseRiskCalculation) => {
    setSelectedItem(item);
    setIsDeleteDialogOpen(true);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ro-RO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRiskLevel = (result?: { riskScore?: number; riskLevel?: string }) => {
    if (!result) return null;
    const level = result.riskLevel || 'Unknown';
    const score = result.riskScore;

    let variant: 'default' | 'secondary' | 'destructive' | 'outline' = 'secondary';
    if (level.toLowerCase().includes('high') || level.toLowerCase().includes('ridicat')) {
      variant = 'destructive';
    } else if (level.toLowerCase().includes('low') || level.toLowerCase().includes('scăzut')) {
      variant = 'default';
    }

    return (
      <div className="flex items-center gap-2">
        <Badge variant={variant}>{level}</Badge>
        {score !== undefined && <span className="text-sm text-muted-foreground">({score}%)</span>}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Calcule Risc</h1>
          <p className="text-muted-foreground mt-2">Vizualizați calculele de risc efectuate.</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Lista Calcule Risc
          </CardTitle>
          <CardDescription>{totalCount} calcule în total</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Caută calcule..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className="pl-10"
              />
              {searchInput && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                  onClick={clearSearch}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <Button onClick={handleSearch} variant="secondary">
              <Search className="h-4 w-4 mr-2" />
              Caută
            </Button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Calculator className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {searchTerm ? 'Nu s-au găsit calcule.' : 'Nu există calcule.'}
              </p>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tip Calcul</TableHead>
                      <TableHead>Versiune Algoritm</TableHead>
                      <TableHead>Nivel Risc</TableHead>
                      <TableHead>Calculat la</TableHead>
                      <TableHead className="w-[100px]">Acțiuni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">
                          {item.calculationType || '-'}
                        </TableCell>
                        <TableCell>
                          {item.algorithmVersion ? (
                            <Badge variant="outline">{item.algorithmVersion}</Badge>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>{getRiskLevel(item.result) || '-'}</TableCell>
                        <TableCell>{formatDate(item.calculatedAt)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => openViewDialog(item)}
                              title="Vizualizează"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => openDeleteDialog(item)}
                              title="Șterge"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Rânduri pe pagină:</span>
                  <Select
                    value={pageSize.toString()}
                    onValueChange={(value) => updateParams({ size: parseInt(value, 10), page: 1 })}
                  >
                    <SelectTrigger className="w-[70px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZE_OPTIONS.map((size) => (
                        <SelectItem key={size} value={size.toString()}>
                          {size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    Pagina {page} din {totalPages}
                  </span>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateParams({ page: page - 1 })}
                      disabled={page <= 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateParams({ page: page + 1 })}
                      disabled={page >= totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Detalii Calcul Risc
            </DialogTitle>
            <DialogDescription>Informații despre calculul de risc.</DialogDescription>
          </DialogHeader>
          {selectedItem && (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Tip Calcul</p>
                  <p className="text-sm">{selectedItem.calculationType || '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Versiune Algoritm</p>
                  <p className="text-sm">{selectedItem.algorithmVersion || '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Calculat la</p>
                  <p className="text-sm">{formatDate(selectedItem.calculatedAt)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Nivel Risc</p>
                  {getRiskLevel(selectedItem.result) || <p className="text-sm">-</p>}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">ID Sesiune Clinică</p>
                <p className="text-sm font-mono">{selectedItem.clinicalSessionId}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">ID Sesiune Evaluare</p>
                <p className="text-sm font-mono">{selectedItem.assessmentExchangeId}</p>
              </div>
              {selectedItem.result && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Rezultat Detaliat</p>
                  <div className="bg-muted p-3 rounded text-sm font-mono overflow-auto max-h-48">
                    <pre>{JSON.stringify(selectedItem.result, null, 2)}</pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmați ștergerea</AlertDialogTitle>
            <AlertDialogDescription>
              Sunteți sigur că doriți să ștergeți acest calcul de risc? Această acțiune nu poate fi
              anulată.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedItem(null)}>Anulează</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Se șterge...
                </>
              ) : (
                'Șterge'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
